/**
 * O menu do painel — spec 0087.
 *
 * Uma árvore só: setor → módulo → aba. É dela que saem os três andares do menu
 * do topo, a URL de cada tela, quem pode ver o quê e a tela de entrada.
 *
 * **Por que existe.** Até 29/09/2026 o painel tinha duas navegações:
 * - o menu do topo (`Dashboard.tsx`), que guardava setor, módulo e aba em
 *   `useState`: F5 voltava ao Monitor, o Voltar do navegador saía do painel e
 *   não havia link para tela nenhuma;
 * - a barra lateral (`navigationConfig.ts`), com URL, mas com outra árvore e
 *   outras permissões — negava o Fretamento a quem o topo mostrava, e esquecia
 *   as Pendências de Cadastro.
 *
 * Ficou o menu do topo, sobre URLs. As regras são as do topo, reescritas aqui
 * uma a uma; a lateral saiu. Os links que a lateral deixou salvos continuam
 * valendo (`ROTAS_ANTIGAS`).
 *
 * Arquivo sem React, para o teste da raiz (`jest`) ler a mesma árvore.
 */
import { hasPermission, podeVer } from '../utils/permissions';

// ── Quem pode ─────────────────────────────────────────────────────────────

/** O que a pessoa pode, perguntado do jeito que o menu pergunta. */
export interface Acesso {
  admin: boolean;
  /** `canView` do `usePermissions` (ver `podeVer`). */
  ve: (setor: string, modulo?: string, aba?: string) => boolean;
  /** Uma permissão exata, como a da API. */
  tem: (permissao: string) => boolean;
}

export function acessoDe(permissoes: string[], admin: boolean): Acesso {
  return {
    admin,
    ve: (setor, modulo, aba) => admin || podeVer(permissoes, setor, modulo, aba),
    tem: (permissao) => admin || hasPermission(permissoes, permissao),
  };
}

type Regra = (a: Acesso) => boolean;

const sempre: Regra = () => true;
const ve = (setor: string, modulo?: string, aba?: string): Regra => (a) => a.ve(setor, modulo, aba);
const tem = (permissao: string): Regra => (a) => a.tem(permissao);

/**
 * A roteirização abre para quem a API deixa passar (0066): uma permissão `web:`
 * paralela poderia divergir, e a tela apareceria para quem recebe 403. O setor
 * usa a mesma regra, e não a lista de setores conhecidos (`getVisibleSectors`),
 * que mostra três a quem não é administrador — foi por ela que a roteirização
 * morou em Processos até a 0098.
 */
const ROTEIRIZACAO = tem('api:roteirizacao:read');

// ── A árvore ──────────────────────────────────────────────────────────────
//
// Os rótulos seguem o vocabulário do painel (`VOCABULARIO.md`, spec 0098): caixa
// de frase, sem abreviação, sem "Dashboard" nem "Gestão de". A catraca
// `vocabulario-do-menu.test.ts` recusa a volta. Fora do que muda de lugar, o
// rótulo muda e o id fica: o id é endereço, e o endereço é contrato.

export interface Aba {
  id: string;
  rotulo: string;
  /** Chave em `routeComponents.ts`. */
  componente: string;
  regra: Regra;
  /** Tem URL, mas não aparece na faixa de abas. */
  oculta?: boolean;
  /**
   * O que pede ação nesta tela, contado pela fonte que o setor registra em
   * `config/contadores.ts` (spec 0098, DD-03). Zero não mostra número.
   */
  contador?: string;
}

/** Um módulo tem abas, ou é ele mesmo a tela (`componente`). */
export interface Modulo {
  id: string;
  rotulo: string;
  regra: Regra;
  abas?: Aba[];
  componente?: string;
}

export interface Setor {
  id: string;
  rotulo: string;
  regra: Regra;
  modulos: Modulo[];
}

export const MENU: Setor[] = [
  {
    id: 'corporativo',
    rotulo: 'Corporativo',
    regra: ve('corporativo'),
    modulos: [
      {
        id: 'monitor',
        rotulo: 'Monitor',
        regra: sempre,
        abas: [
          { id: 'monitor', rotulo: 'Monitor de operações', componente: 'Monitor', regra: sempre },
          // A tela mudou de módulo (saiu de Processos), a permissão ficou a mesma:
          // mover a permissão junto exigiria migration e mudaria quem vê a aba.
          { id: 'notificacoes', rotulo: 'Comunicados', componente: 'Notificacoes', regra: ve('corporativo', 'processos', 'notificacoes') },
          // Spec 0037: a permissão é a do segmento `ajuda`, não a do Monitor.
          { id: 'central-ajuda', rotulo: 'Central de ajuda', componente: 'CentralAjudaAdmin', regra: ve('corporativo', 'ajuda') },
          // Spec 0029: o Monitor 2.0 está fora do menu. A tela segue inteira, com URL.
          { id: 'monitor-v2', rotulo: 'Monitor 2.0', componente: 'MonitorV2', regra: sempre, oculta: true },
          // 0111: a permissão é a do cadastro de alunos, a mesma da API (DD-16).
          // 0112: a fila ganhou tela própria; esta conta só as planilhas que pedem ação.
          { id: 'planilhas-escolas', rotulo: 'Planilhas das escolas', componente: 'PlanilhasDasEscolas', regra: tem('api:transportador:read'), contador: 'planilhas' },
          { id: 'pendencias-planilhas', rotulo: 'Decidir pendências das planilhas', componente: 'DecidirPendencias', regra: tem('api:transportador:read'), contador: 'pendenciasDasPlanilhas' },
        ],
      },
      {
        id: 'cadastros',
        rotulo: 'Cadastros',
        regra: ve('corporativo', 'cadastros'),
        abas: [
          { id: 'alunos', rotulo: 'Alunos', componente: 'CadastrosAlunos', regra: ve('corporativo', 'cadastros', 'alunos') },
          { id: 'escolas', rotulo: 'Escolas', componente: 'CadastrosEscolas', regra: ve('corporativo', 'cadastros', 'escolas') },
          { id: 'transportador', rotulo: 'Transportadores', componente: 'CadastrosTransportador', regra: ve('corporativo', 'cadastros', 'transportador') },
          { id: 'veiculos', rotulo: 'Veículos', componente: 'CadastrosVeiculos', regra: ve('corporativo', 'cadastros', 'veiculos') },
          { id: 'pessoas', rotulo: 'Usuários', componente: 'CadastrosPessoas', regra: ve('corporativo', 'cadastros', 'pessoas') },
        ],
      },
      {
        id: 'processos',
        rotulo: 'Processos',
        regra: ve('corporativo', 'processos'),
        abas: [
          { id: 'gestao-fechamento', rotulo: 'Fechamento por escola', componente: 'GestaoFechamento', regra: ve('corporativo', 'processos', 'fechamento') },
          { id: 'gestao-pagamentos', rotulo: 'Pagamento por escola', componente: 'GestaoPagamentos', regra: ve('corporativo', 'processos', 'pagamentos') },
          // 0041: quem administra o fechamento não herda o direito de mexer em alíquota.
          { id: 'fechamento-cooperado', rotulo: 'Fechamento por cooperado', componente: 'FechamentoCooperado', regra: ve('corporativo', 'processos', 'pagamentos'), contador: 'notasFiscais' },
          { id: 'parametros-fechamento', rotulo: 'Parâmetros do fechamento', componente: 'ParametrosFechamento', regra: ve('corporativo', 'processos', 'fechamento:parametros') },
          // A roteirização morava aqui até a 0098; hoje é setor (ver abaixo).
        ],
      },
      {
        id: 'relatorios',
        rotulo: 'Relatórios',
        regra: ve('corporativo', 'relatorios'),
        abas: [
          { id: 'demonstrativo-financeiro', rotulo: 'Demonstrativo financeiro', componente: 'DemonstrativoFinanceiro', regra: ve('corporativo', 'relatorios', 'demonstrativos') },
          { id: 'demonstrativo-analitico', rotulo: 'Demonstrativo analítico', componente: 'DemonstrativoAnalitico', regra: ve('corporativo', 'relatorios', 'demonstrativos') },
          { id: 'demonstrativo-sintetico', rotulo: 'Demonstrativo sintético', componente: 'DemonstrativoSintetico', regra: ve('corporativo', 'relatorios', 'demonstrativos') },
          // As listas de conferência usam a permissão dos demonstrativos (mesmo submenu).
          { id: 'lista-alunos-escolas', rotulo: 'Alunos por escola', componente: 'ListaAlunosEscolas', regra: ve('corporativo', 'relatorios', 'demonstrativos') },
          { id: 'lista-alunos-transportadores', rotulo: 'Alunos por transportador', componente: 'ListaAlunosTransportadores', regra: ve('corporativo', 'relatorios', 'demonstrativos') },
        ],
      },
      {
        // Spec 0108: o que quem usa o painel, o portal e o app manda à equipe. A aba
        // abre pela mesma permissão da API, como a roteirização (0066): uma
        // permissão de tela paralela poderia mostrar a aba a quem recebe 403.
        id: 'atendimento',
        rotulo: 'Atendimento',
        regra: tem('api:feedback:atender'),
        abas: [
          { id: 'feedback', rotulo: 'Feedback', componente: 'FilaFeedback', regra: tem('api:feedback:atender'), contador: 'feedback' },
          // Spec 0121: documentos, denúncias e chamados do app Vai de Escolar, com quem já atende.
          { id: 'vai-de-escolar', rotulo: 'Vai de Escolar', componente: 'OperacaoVaiDeEscolar', regra: tem('api:feedback:atender') },
        ],
      },
    ],
  },
  {
    // O id de URL é `integracao`, como na lateral; o nome na tela é Escolar, e a
    // permissão é `web:escolar:*` (spec 0078).
    id: 'integracao',
    rotulo: 'Escolar',
    regra: ve('escolar'),
    modulos: [
      {
        id: 'escola',
        rotulo: 'Escola',
        regra: ve('escolar', 'escola'),
        abas: [
          { id: 'dashboard-escola', rotulo: 'Visão geral', componente: 'EscolaDashboard', regra: ve('escolar', 'escola', 'dashboard') },
          { id: 'dados-escola', rotulo: 'Dados', componente: 'EscolaDados', regra: ve('escolar', 'escola', 'dados') },
        ],
      },
      {
        id: 'transportador',
        rotulo: 'Transportador',
        regra: ve('escolar', 'transportador'),
        abas: [
          { id: 'cadastro-manual', rotulo: 'Cadastro manual', componente: 'CadastroManualAlunos', regra: ve('escolar', 'transportador', 'cadastro') },
        ],
      },
    ],
  },
  {
    id: 'fretamento',
    rotulo: 'Fretamento',
    regra: ve('fretamento'),
    modulos: [
      {
        id: 'cte',
        rotulo: 'CT-e',
        regra: sempre,
        abas: [
          { id: 'monitor-fret', rotulo: 'Monitor', componente: 'MonitorFretamento', regra: sempre },
          { id: 'emissao', rotulo: 'Emissão', componente: 'EmissaoCTeOS', regra: ve('fretamento', 'emissao') },
          { id: 'consulta', rotulo: 'Consulta', componente: 'ConsultaCTeOS', regra: ve('fretamento', 'consulta') },
          { id: 'relatorios-fret', rotulo: 'Relatórios', componente: 'RelatoriosFretamento', regra: ve('fretamento', 'relatorios') },
          { id: 'fiscal-fret', rotulo: 'Faturamento', componente: 'FiscalDashboard', regra: ve('fretamento', 'monitor') },
          // Os parâmetros atravessam CT-e e ATF: a mesma tela nos dois módulos.
          { id: 'parametros-emissao', rotulo: 'Parâmetros', componente: 'ParametrosEmissao', regra: ve('fretamento', 'monitor') },
        ],
      },
      {
        id: 'atf',
        rotulo: 'ATF',
        regra: sempre,
        abas: [
          { id: 'monitor-atf', rotulo: 'Monitor', componente: 'MonitorAtf', regra: ve('fretamento', 'monitor') },
          { id: 'emissao-atf', rotulo: 'Emissão', componente: 'EmissaoAtf', regra: ve('fretamento', 'emissao') },
          { id: 'planos-atf', rotulo: 'Tarifas', componente: 'PlanosTarifaAtf', regra: ve('fretamento', 'monitor') },
          // O crédito pré-pago é por km rodado, e o km vem da rota da ATF.
          { id: 'creditos-atf', rotulo: 'Créditos', componente: 'CreditosDashboard', regra: ve('fretamento', 'monitor') },
          { id: 'parametros-emissao', rotulo: 'Parâmetros', componente: 'ParametrosEmissao', regra: ve('fretamento', 'monitor') },
        ],
      },
      {
        // Spec 0028: as lacunas de veículo e de condutor aparecem juntas numa tela só.
        id: 'pendencias-cadastro',
        rotulo: 'Pendências de cadastro',
        regra: ve('fretamento', 'monitor'),
        componente: 'PendenciasCadastro',
      },
    ],
  },
  {
    id: 'vivan',
    rotulo: 'ViVan',
    regra: ve('vivan'),
    modulos: [
      { id: 'dashboard', rotulo: 'Visão geral', regra: sempre, componente: 'VivanDashboard' },
      { id: 'passageiros', rotulo: 'Passageiros', regra: ve('vivan', 'passageiros'), componente: 'PassageirosLista' },
      { id: 'contratos', rotulo: 'Contratos', regra: ve('vivan', 'contratos'), componente: 'ContratosLista' },
      {
        id: 'financeiro',
        rotulo: 'Financeiro',
        regra: ve('vivan', 'financeiro'),
        abas: [
          { id: 'mensalidades', rotulo: 'Mensalidades', componente: 'MensalidadesLista', regra: ve('vivan', 'financeiro', 'mensalidades') },
        ],
      },
    ],
  },
  {
    // 0098 (ADR-0013): o setor da roteirização escolar, com uma tela por aba. As
    // seções que moravam numa aba de Processos viraram módulos e abas daqui. O
    // número do que pede ação vem da visão geral (`Aba.contador`).
    id: 'roteirizacao',
    rotulo: 'Roteirização',
    regra: ROTEIRIZACAO,
    modulos: [
      { id: 'visao-geral', rotulo: 'Visão geral', regra: ROTEIRIZACAO, componente: 'RoteirizacaoVisaoGeral' },
      {
        // O que pede resposta em minutos e se relê sozinho.
        id: 'operacao',
        rotulo: 'Operação',
        regra: ROTEIRIZACAO,
        abas: [
          { id: 'viagens', rotulo: 'Viagens', componente: 'RoteirizacaoViagens', regra: ROTEIRIZACAO },
          { id: 'atendimento', rotulo: 'Atendimento', componente: 'RoteirizacaoAtendimento', regra: ROTEIRIZACAO, contador: 'atendimento' },
          { id: 'ocorrencias', rotulo: 'Ocorrências', componente: 'RoteirizacaoOcorrencias', regra: ROTEIRIZACAO, contador: 'ocorrencias' },
        ],
      },
      {
        // O que se prepara antes do dia.
        id: 'planejamento',
        rotulo: 'Planejamento',
        regra: ROTEIRIZACAO,
        abas: [
          { id: 'rotas', rotulo: 'Rotas', componente: 'RoteirizacaoRotas', regra: ROTEIRIZACAO },
          { id: 'calendario', rotulo: 'Calendário', componente: 'RoteirizacaoCalendario', regra: ROTEIRIZACAO },
          { id: 'prontidao', rotulo: 'Prontidão', componente: 'RoteirizacaoProntidao', regra: ROTEIRIZACAO, contador: 'prontidao' },
          // Alunos e escolas (0116). A parte aberta vai em `?parte=` (DD-12).
          { id: 'coordenadas', rotulo: 'Coordenadas', componente: 'RoteirizacaoCoordenadas', regra: ROTEIRIZACAO, contador: 'coordenadas' },
        ],
      },
      {
        // Quem cuida da criança e onde ela embarca: os pedidos chegam por cinco portas.
        id: 'passageiros',
        rotulo: 'Passageiros',
        regra: ROTEIRIZACAO,
        abas: [
          { id: 'pedidos', rotulo: 'Pedidos', componente: 'RoteirizacaoPedidos', regra: ROTEIRIZACAO, contador: 'pedidos' },
          { id: 'guarda', rotulo: 'Guarda compartilhada', componente: 'RoteirizacaoGuarda', regra: ROTEIRIZACAO },
          { id: 'importar-responsaveis', rotulo: 'Importar responsáveis', componente: 'RoteirizacaoImportarResponsaveis', regra: ROTEIRIZACAO },
        ],
      },
      {
        // O que muda o comportamento.
        id: 'configuracao',
        rotulo: 'Configuração',
        regra: ROTEIRIZACAO,
        abas: [
          { id: 'parametros', rotulo: 'Parâmetros', componente: 'RoteirizacaoParametros', regra: ROTEIRIZACAO },
          { id: 'whatsapp', rotulo: 'WhatsApp', componente: 'RoteirizacaoWhatsapp', regra: ROTEIRIZACAO },
          { id: 'privacidade', rotulo: 'Privacidade', componente: 'RoteirizacaoPrivacidade', regra: ROTEIRIZACAO },
        ],
      },
    ],
  },
];

/** A tela em que o painel abre, para quem pode vê-la. */
export const TELA_INICIAL = '/corporativo/monitor/monitor';

/**
 * Os endereços que a barra lateral deixou em favoritos e mensagens e que a
 * árvore do topo arruma de outro jeito. Os demais continuam iguais, ou caem
 * na regra geral de `resolver` (segmento a mais sai, segmento desconhecido
 * sobe um andar).
 */
export const ROTAS_ANTIGAS: Record<string, string> = {
  '/fretamento/config/parametros-emissao': '/fretamento/cte/parametros-emissao',
  '/fretamento/config/creditos-atf': '/fretamento/atf/creditos-atf',
  '/vivan/financeiro/dashboard-vivan': '/vivan/dashboard',
  '/configuracoes/perfil/perfil': '/',
  // 0111: as duas importações de aluno saíram; quem tinha o endereço chega às planilhas das escolas.
  '/integracao/transportador/importar': '/corporativo/monitor/planilhas-escolas',
  // 0098: o Roteirizador antigo saiu do menu (0 cenários, nada desde 15/05). Os
  // endereços dele, os de hoje e os que a lateral deixou, levam ao equivalente.
  '/roteirizador': '/roteirizacao/visao-geral',
  '/roteirizador/dashboard': '/roteirizacao/visao-geral',
  '/roteirizador/rotas/dashboard-rot': '/roteirizacao/visao-geral',
  '/roteirizador/rotas': '/roteirizacao/planejamento/rotas',
  '/roteirizador/rotas/rotas': '/roteirizacao/planejamento/rotas',
  '/roteirizador/geocodificacao': '/roteirizacao/planejamento/coordenadas',
  '/roteirizador/geocodificacao/geocodificacao': '/roteirizacao/planejamento/coordenadas',
  // O parâmetro do algoritmo não existe mais: os da roteirização são o equivalente.
  '/roteirizador/parametros': '/roteirizacao/configuracao/parametros',
  '/roteirizador/parametros/parametros': '/roteirizacao/configuracao/parametros',
};

/**
 * A seção da roteirização — o `?secao=` dos endereços de antes da 0098, e o
 * nome que as telas da roteirização usam entre si — e o componente da tela.
 * Coordenadas nasceu na 0098 e não tem endereço antigo com seção. O endereço sai da árvore (`caminhoDoComponente`): uma
 * tabela só serve à navegação entre as telas e ao endereço antigo.
 */
export const SECOES_DA_ROTEIRIZACAO: Record<string, string> = {
  visao: 'RoteirizacaoVisaoGeral',
  viagens: 'RoteirizacaoViagens',
  atendimento: 'RoteirizacaoAtendimento',
  excecoes: 'RoteirizacaoOcorrencias',
  rotas: 'RoteirizacaoRotas',
  calendario: 'RoteirizacaoCalendario',
  prontidao: 'RoteirizacaoProntidao',
  validacao: 'RoteirizacaoPedidos',
  guarda: 'RoteirizacaoGuarda',
  planilha: 'RoteirizacaoImportarResponsaveis',
  parametros: 'RoteirizacaoParametros',
  canal: 'RoteirizacaoWhatsapp',
  privacidade: 'RoteirizacaoPrivacidade',
  coordenadas: 'RoteirizacaoCoordenadas',
};

/** O endereço da roteirização até a 0098, com a seção na busca. */
const ROTEIRIZACAO_ANTIGA = '/corporativo/processos/roteirizacao';
/** O item aberto que segue com a seção: a viagem só para Viagens, a rota só para Rotas. */
const ITEM_DA_SECAO: Record<string, string> = { viagens: 'viagem', rotas: 'rota' };

/** `/corporativo/processos/roteirizacao?secao=viagens&viagem=48` → a tela nova, com a viagem. */
function destinoDaRoteirizacaoAntiga(busca: string, arvore: Setor[]): string | null {
  const antiga = new URLSearchParams(busca);
  const secao = antiga.get('secao') ?? 'visao';
  const componente = SECOES_DA_ROTEIRIZACAO[secao] ?? SECOES_DA_ROTEIRIZACAO.visao;
  const caminho = caminhoDoComponente(componente, arvore);
  if (!caminho) return null;
  const item = ITEM_DA_SECAO[secao];
  const valor = item ? antiga.get(item) : null;
  return item && valor ? `${caminho}?${new URLSearchParams({ [item]: valor }).toString()}` : caminho;
}

// ── O menu de uma pessoa ──────────────────────────────────────────────────

/**
 * A árvore podada pelo que a pessoa pode.
 *
 * Um módulo com abas só aparece se ao menos uma aba aparece, e um setor só se
 * ao menos um módulo aparece: o topo desenhava módulo vazio, que abria tela em
 * branco. As abas ocultas continuam na árvore (têm URL), fora da faixa.
 */
export function menuDe(acesso: Acesso, menu: Setor[] = MENU): Setor[] {
  const pode = (r: Regra) => acesso.admin || r(acesso);
  return menu
    .filter((s) => pode(s.regra))
    .map((s) => ({
      ...s,
      modulos: s.modulos
        .filter((m) => pode(m.regra))
        .map((m) => (m.abas ? { ...m, abas: m.abas.filter((a) => pode(a.regra)) } : m))
        .filter((m) => !m.abas || m.abas.length > 0),
    }))
    .filter((s) => s.modulos.length > 0);
}

/** As abas que vão na faixa (sem as ocultas). */
export const abasDaFaixa = (m: Modulo): Aba[] => (m.abas ?? []).filter((a) => !a.oculta);

export const caminhoDoModulo = (s: Setor, m: Modulo): string => {
  const primeira = abasDaFaixa(m)[0] ?? m.abas?.[0];
  return primeira ? `/${s.id}/${m.id}/${primeira.id}` : `/${s.id}/${m.id}`;
};

export const caminhoDoSetor = (s: Setor): string => caminhoDoModulo(s, s.modulos[0]);

/**
 * O endereço de uma tela pelo componente dela (spec 0098). Quem navega de uma
 * tela para outra pergunta aqui, em vez de escrever o caminho: a árvore é a
 * única que sabe onde cada tela mora.
 */
export function caminhoDoComponente(componente: string, arvore: Setor[] = MENU): string | null {
  for (const s of arvore) {
    for (const m of s.modulos) {
      if (m.componente === componente) return `/${s.id}/${m.id}`;
      const a = m.abas?.find((x) => x.componente === componente);
      if (a) return `/${s.id}/${m.id}/${a.id}`;
    }
  }
  return null;
}

/**
 * O nome da tela com o lugar, para o título da aba do navegador (spec 0098,
 * DD-08): "Pedidos · Passageiros". "Visão geral", "Monitor" e "Emissão" se
 * repetem entre módulos; só o rótulo não distinguiria duas abas abertas.
 */
export const tituloDaTela = (s: Setor, m: Modulo, a?: Aba): string =>
  (a ? `${a.rotulo} · ${m.rotulo}` : `${m.rotulo} · ${s.rotulo}`);

/**
 * A entrada: a tela inicial, se a pessoa pode vê-la; senão, a primeira do menu
 * dela (spec 0078 — a escola entrava num "Acesso negado"); e nada, se o menu
 * dela está vazio.
 */
export function entradaDe(menu: Setor[], preferida: string = TELA_INICIAL): string | null {
  if (menu.length === 0) return null;
  const [s, m, a] = preferida.split('/').filter(Boolean);
  const aba = menu.find((x) => x.id === s)?.modulos.find((x) => x.id === m)?.abas?.find((x) => x.id === a);
  return aba ? preferida : caminhoDoSetor(menu[0]);
}

// ── Da URL para a tela ────────────────────────────────────────────────────

export type Resolucao =
  | { tipo: 'tela'; setor: Setor; modulo: Modulo; aba?: Aba; componente: string }
  | { tipo: 'ir'; para: string }
  | { tipo: 'sem-acesso' };

/**
 * O que um endereço abre, para quem tem este menu.
 *
 * - endereço de tela que a pessoa pode ver: a tela;
 * - endereço de tela que ela não pode: `sem-acesso` — não a mandamos em
 *   silêncio para outro lugar, porque um link colado precisa dizer por que não
 *   abriu;
 * - setor ou módulo sem aba: a primeira tela que ela pode ver ali;
 * - endereço antigo da lateral: o equivalente;
 * - o que não existe: a entrada.
 *
 * A busca (`?viagem=48`) vai junto em todo redirecionamento: é ela que leva o
 * item aberto. O `resolver` é o único dono dela (spec 0098, DD-04) — a casca
 * navega para `para` como veio, sem somar a busca de novo.
 */
export function resolver(caminho: string, menu: Setor[], arvore: Setor[] = MENU, busca = ''): Resolucao {
  const limpo = caminho.replace(/\/+$/, '') || '/';
  const comBusca = busca && !busca.startsWith('?') ? `?${busca}` : busca;
  const ir = (para: string): Resolucao => ({ tipo: 'ir', para: para + comBusca });
  if (limpo === ROTEIRIZACAO_ANTIGA) {
    const para = destinoDaRoteirizacaoAntiga(comBusca, arvore);
    if (para) return { tipo: 'ir', para };
  }
  if (ROTAS_ANTIGAS[limpo]) return ir(ROTAS_ANTIGAS[limpo]);

  const [sId, mId, aId, ...resto] = limpo.split('/').filter(Boolean);
  const entrada = (): Resolucao => {
    const para = entradaDe(menu);
    return para ? ir(para) : { tipo: 'sem-acesso' };
  };

  const setor = arvore.find((s) => s.id === sId);
  if (!setor) return entrada();
  const setorVisto = menu.find((s) => s.id === sId);

  const modulo = setor.modulos.find((m) => m.id === mId);
  if (!modulo) {
    if (mId === undefined) return setorVisto ? ir(caminhoDoSetor(setorVisto)) : { tipo: 'sem-acesso' };
    return ir(`/${sId}`);
  }
  const moduloVisto = setorVisto?.modulos.find((m) => m.id === mId);

  if (!modulo.abas) {
    if (aId !== undefined) return ir(`/${sId}/${mId}`);
    return moduloVisto
      ? { tipo: 'tela', setor: setorVisto!, modulo: moduloVisto, componente: modulo.componente! }
      : { tipo: 'sem-acesso' };
  }

  if (aId === undefined) {
    return moduloVisto ? ir(caminhoDoModulo(setorVisto!, moduloVisto)) : { tipo: 'sem-acesso' };
  }
  const aba = modulo.abas.find((a) => a.id === aId);
  if (!aba) return ir(`/${sId}/${mId}`);
  if (resto.length > 0) return ir(`/${sId}/${mId}/${aId}`);

  const abaVista = moduloVisto?.abas?.find((a) => a.id === aId);
  return abaVista
    ? { tipo: 'tela', setor: setorVisto!, modulo: moduloVisto!, aba: abaVista, componente: abaVista.componente }
    : { tipo: 'sem-acesso' };
}
